// Ported 1:1 from ~/Desktop/rondo/src/components/ui/Tooltip.tsx.
// Includes the `tooltipSurfaceClass` constant used by Liveline.

import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { motion } from 'motion/react'

export const tooltipSurfaceClass =
  'rounded-full bg-lightest-grey text-black border border-light-grey px-4 py-3 text-body shadow-big tabular-nums whitespace-nowrap text-trim'

type Side = 'top' | 'bottom' | 'left' | 'right'
type Align = 'start' | 'center' | 'end'

interface TooltipProps {
  content: ReactNode
  side?: Side
  align?: Align
  offset?: number
  openDelay?: number
  closeDelay?: number
  disabled?: boolean
  className?: string
  children: ReactNode
}

export function Tooltip({
  content,
  side = 'top',
  align = 'center',
  offset = 8,
  openDelay = 150,
  closeDelay = 0,
  disabled = false,
  className,
  children,
}: TooltipProps) {
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [present, setPresent] = useState(false)
  const [visible, setVisible] = useState(false)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const [resolvedSide, setResolvedSide] = useState<Side>(side)
  const wrapperRef = useRef<HTMLSpanElement>(null)
  const surfaceRef = useRef<HTMLDivElement>(null)
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const id = useId()

  useEffect(() => setMounted(true), [])

  useEffect(() => {
    if (open) setPresent(true)
  }, [open])

  useEffect(() => {
    if (open && present) {
      let raf2 = 0
      const raf1 = requestAnimationFrame(() => {
        raf2 = requestAnimationFrame(() => setVisible(true))
      })
      return () => {
        cancelAnimationFrame(raf1)
        if (raf2) cancelAnimationFrame(raf2)
      }
    }
    if (!open) setVisible(false)
    return undefined
  }, [open, present])

  useEffect(() => {
    return () => {
      if (openTimer.current) clearTimeout(openTimer.current)
      if (closeTimer.current) clearTimeout(closeTimer.current)
    }
  }, [])

  function scheduleOpen() {
    if (disabled) return
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
    if (openDelay <= 0) {
      setOpen(true)
      return
    }
    openTimer.current = setTimeout(() => setOpen(true), openDelay)
  }

  function scheduleClose() {
    if (openTimer.current) {
      clearTimeout(openTimer.current)
      openTimer.current = null
    }
    if (closeDelay <= 0) {
      setOpen(false)
      return
    }
    closeTimer.current = setTimeout(() => setOpen(false), closeDelay)
  }

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  useLayoutEffect(() => {
    if (!open || !wrapperRef.current) return
    function update() {
      if (!wrapperRef.current) return
      const wr = wrapperRef.current.getBoundingClientRect()
      const sw = surfaceRef.current?.offsetWidth ?? 0
      const sh = surfaceRef.current?.offsetHeight ?? 0
      const { side: finalSide, top, left } = computePlacement({
        wrapper: wr,
        surfaceW: sw,
        surfaceH: sh,
        side,
        align,
        offset,
      })
      setPos({ top, left })
      setResolvedSide(finalSide)
    }
    update()
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
    }
  }, [open, side, align, offset])

  useLayoutEffect(() => {
    if (!open || !surfaceRef.current || !wrapperRef.current || !pos) return
    const wr = wrapperRef.current.getBoundingClientRect()
    const sw = surfaceRef.current.offsetWidth
    const sh = surfaceRef.current.offsetHeight
    if (!sw || !sh) return
    const { side: finalSide, top, left } = computePlacement({
      wrapper: wr,
      surfaceW: sw,
      surfaceH: sh,
      side,
      align,
      offset,
    })
    if (top !== pos.top || left !== pos.left || finalSide !== resolvedSide) {
      setPos({ top, left })
      setResolvedSide(finalSide)
    }
  }, [open, pos, resolvedSide, side, align, offset])

  const transformOrigin = TRANSFORM_ORIGIN[resolvedSide]
  const exitOffset = EXIT_OFFSET[resolvedSide]

  return (
    <>
      <span
        ref={wrapperRef}
        className="inline-flex"
        onMouseEnter={scheduleOpen}
        onMouseLeave={scheduleClose}
        onFocus={scheduleOpen}
        onBlur={scheduleClose}
        aria-describedby={open ? id : undefined}
      >
        {children}
      </span>
      {mounted &&
        present &&
        createPortal(
          <motion.div
            key="tooltip"
            id={id}
            ref={surfaceRef}
            role="tooltip"
            variants={{
              hidden: { opacity: 0, scale: 0.96, ...exitOffset },
              shown: { opacity: 1, scale: 1, x: 0, y: 0 },
            }}
            initial="hidden"
            animate={visible ? 'shown' : 'hidden'}
            transition={{ type: 'spring', stiffness: 500, damping: 36 }}
            onAnimationComplete={(definition) => {
              if (definition === 'hidden' && !open) setPresent(false)
            }}
            style={{
              position: 'fixed',
              top: pos?.top ?? -9999,
              left: pos?.left ?? -9999,
              transformOrigin,
              zIndex: 1001,
              pointerEvents: 'none',
            }}
            className={[tooltipSurfaceClass, className ?? ''].join(' ').trim()}
          >
            {content}
          </motion.div>,
          document.body,
        )}
    </>
  )
}

interface PlacementInput {
  wrapper: DOMRect
  surfaceW: number
  surfaceH: number
  side: Side
  align: Align
  offset: number
}

function computePlacement({
  wrapper,
  surfaceW,
  surfaceH,
  side,
  align,
  offset,
}: PlacementInput): { side: Side; top: number; left: number } {
  const vw = typeof window === 'undefined' ? 1024 : window.innerWidth
  const vh = typeof window === 'undefined' ? 768 : window.innerHeight

  const spaceTop = wrapper.top
  const spaceBottom = vh - wrapper.bottom
  const spaceLeft = wrapper.left
  const spaceRight = vw - wrapper.right
  const needVertical = surfaceH + offset + 8
  const needHorizontal = surfaceW + offset + 8

  let chosen: Side = side
  if (side === 'top' && spaceTop < needVertical && spaceBottom > spaceTop) {
    chosen = 'bottom'
  } else if (side === 'bottom' && spaceBottom < needVertical && spaceTop > spaceBottom) {
    chosen = 'top'
  } else if (side === 'left' && spaceLeft < needHorizontal && spaceRight > spaceLeft) {
    chosen = 'right'
  } else if (side === 'right' && spaceRight < needHorizontal && spaceLeft > spaceRight) {
    chosen = 'left'
  }

  let top = 0
  let left = 0
  if (chosen === 'top') {
    top = wrapper.top - surfaceH - offset
    left = alignHorizontal(wrapper, surfaceW, align)
  } else if (chosen === 'bottom') {
    top = wrapper.bottom + offset
    left = alignHorizontal(wrapper, surfaceW, align)
  } else if (chosen === 'left') {
    top = alignVertical(wrapper, surfaceH, align)
    left = wrapper.left - surfaceW - offset
  } else {
    top = alignVertical(wrapper, surfaceH, align)
    left = wrapper.right + offset
  }

  left = Math.max(4, Math.min(vw - surfaceW - 4, left))
  top = Math.max(4, Math.min(vh - surfaceH - 4, top))

  return { side: chosen, top: Math.round(top), left: Math.round(left) }
}

function alignHorizontal(wr: DOMRect, sw: number, align: Align): number {
  if (align === 'start') return wr.left
  if (align === 'end') return wr.right - sw
  return wr.left + wr.width / 2 - sw / 2
}

function alignVertical(wr: DOMRect, sh: number, align: Align): number {
  if (align === 'start') return wr.top
  if (align === 'end') return wr.bottom - sh
  return wr.top + wr.height / 2 - sh / 2
}

const TRANSFORM_ORIGIN: Record<Side, string> = {
  top: 'bottom center',
  bottom: 'top center',
  left: 'center right',
  right: 'center left',
}

const EXIT_OFFSET: Record<Side, { x?: number; y?: number }> = {
  top: { y: 4 },
  bottom: { y: -4 },
  left: { x: 4 },
  right: { x: -4 },
}
